import { StandardEvents } from '@shopify/events';

/**
 * Wings: "Notify me when it's back" (blocks/wings-notify-me.liquid).
 * Sends the shopper's email and the chosen button to Klaviyo's client back-in-stock subscription
 * endpoint (built for storefront use with the public site ID). Klaviyo's Back in stock flow then
 * sends one email when that button is back in stock. No marketing consent is asked for or given.
 *
 * The variant picker only re-renders itself, so when another button is picked this swaps in the
 * block's content for that button from the section HTML (like Atelier's inventory block). Buttons
 * signed up for in this visit are remembered for the tab, so returning to one shows the note again.
 */
const SIGNED_UP_KEY = 'wings-notify-signed-up';

/** @returns {string[]} */
function readSignedUp() {
  try {
    const list = JSON.parse(sessionStorage.getItem(SIGNED_UP_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/** @param {string} variant */
function rememberSignedUp(variant) {
  try {
    const list = readSignedUp();
    if (!list.includes(variant)) sessionStorage.setItem(SIGNED_UP_KEY, JSON.stringify([...list, variant]));
  } catch {
    // storage blocked: the note still shows until the page changes
  }
}

class WingsNotifyMe extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.addEventListener('submit', this.#handleSubmit);
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#showRemembered();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#handleSubmit);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#controller?.abort();
  }

  #showDone() {
    const details = this.querySelector('.wings-notify__details');
    const done = this.querySelector('.wings-notify__done');
    if (details instanceof HTMLElement) details.hidden = true;
    if (done instanceof HTMLElement) done.hidden = false;
    return done;
  }

  #showRemembered() {
    const { variant } = this.dataset;
    if (variant && readSignedUp().includes(variant)) this.#showDone();
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        if (!detail.newProduct && detail.productId && detail.productId !== this.dataset.productId) return;

        const next = detail.html.querySelector(`wings-notify-me[data-block-id="${this.dataset.blockId}"]`);
        if (!(next instanceof HTMLElement)) return;
        // Same button (e.g. a quantity change re-render): keep what the shopper has open or typed.
        if (next.dataset.variant === this.dataset.variant && next.hidden === this.hidden) return;

        // A sign-up still in flight finishes for the button it was made for (see #handleSubmit).
        this.dataset.variant = next.dataset.variant ?? '';
        if (next.dataset.productId) this.dataset.productId = next.dataset.productId;
        this.hidden = next.hidden;
        this.replaceChildren(...Array.from(next.childNodes, (node) => document.importNode(node, true)));
        this.#showRemembered();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[wings-notify-me]', error);
      });
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('wings-notify__form')) return;
    event.preventDefault();

    const input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="email"]'));
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    const message = form.querySelector('.wings-notify__message');
    if (!input || !button || !message) return;

    if (!input.checkValidity() || !input.value.trim()) {
      input.reportValidity();
      return;
    }

    const { company, variant, error } = this.dataset;
    if (!company || !variant) return;
    // One request at a time: a second Enter while the first is on its way would sign up twice.
    if (this.#controller) return;

    this.#controller = new AbortController();
    button.setAttribute('aria-busy', 'true');
    message.textContent = '';

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(company)}`,
        {
          method: 'POST',
          signal: this.#controller.signal,
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email: input.value.trim() } } },
                channels: ['EMAIL'],
              },
              relationships: { variant: { data: { type: 'catalog-variant', id: variant } } },
            },
          }),
        }
      );
      if (!response.ok) throw new Error(`Klaviyo ${response.status}`);

      rememberSignedUp(variant);
      // The shopper picked another button while this was on its way: remembered, nothing to show here.
      if (this.dataset.variant !== variant) return;
      const done = this.#showDone();
      if (done instanceof HTMLElement) {
        done.setAttribute('tabindex', '-1');
        done.focus();
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      message.textContent = error || "Couldn't sign you up just now. Please try again.";
    } finally {
      this.#controller = null;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('wings-notify-me')) {
  customElements.define('wings-notify-me', WingsNotifyMe);
}
